import React, { useContext } from 'react';
import { useTPAComponentsContext } from '@wix/wix-ui-tpa/cssVars';
import { faqButtonClickSrc63Evid836 } from '@wix/bi-logger-labs-data/v2';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';
import type { Category } from '@wix/ambassador-faq-category-v2-category/types';
import VerticalTabs from './VerticalCategories';
import HorizontalCategories from './HorizontalCategories';
import { useCategories } from '../../../../hooks/useCategories';
import { AppContext } from '../SettingsProvider/SettingsProvider';
import { TabItem } from '@wix/wix-ui-tpa';
import {
  LAST_CATEGORY_ID_FOR_MIGRATION,
  MIGRATION_CATEGORY_CHANGED_THRESHOLD_MS,
  isMigrationStyleCategory,
} from '../../../../constants/migration';
import { getCategoryTitleForOOI } from '../../../../utils/getCategoryTitleForOOI';
import { getCategoryDates } from '../../../../utils/categoryDateUtils';
import settingsParams from '../../settingsParams';

export interface TabsWrapperProps {
  validLists: Category[];
}

const TabsWrapper = ({ validLists }: TabsWrapperProps) => {
  let isMigratingCategory = false;
  const logger = useBi();
  const settings = useSettings();
  const { appDefinitionId } = useEnvironment();
  const { setFirstQuestionId, searchTerm, fetchError, baseURL } =
    useContext(AppContext);
  const { rtl, seo } = useTPAComponentsContext();
  const { t } = useTranslation();

  const {
    categoriesToRender,
    activeTab,
    setActiveTab,
    splitIntoCategories,
    topCategoriesPosition,
    sideCategoriesPosition,
  } = useCategories(validLists);
  const manageCategories = settings.get(settingsParams.manageCategories);
  const savedCategoryIds = manageCategories?.categories ?? [];
  const isAnyCategoryChanged = categoriesToRender.some((category: Category) => {
    const { updated, created } = getCategoryDates(category);
    return (
      created != null &&
      updated != null &&
      Math.abs(+new Date(updated) - +new Date(created)) >
        MIGRATION_CATEGORY_CHANGED_THRESHOLD_MS
    );
  });
  // Hide categories when: (1) saved settings contain *legacy* migration IDs (non-UUID), or
  // (2) all categories are migration placeholders (migrated site: show flat FAQ list, no "General1" tab)
  const hasLegacySavedCategoryId = savedCategoryIds.some(
    (id: string) =>
      typeof id === 'string' &&
      !id.includes('-') &&
      id.length >= 12 &&
      LAST_CATEGORY_ID_FOR_MIGRATION.includes(id.slice(-5)),
  );
  const allCategoriesAreMigrationPlaceholders =
    categoriesToRender.length > 0 &&
    categoriesToRender.every(isMigrationStyleCategory);
  if (
    (savedCategoryIds.length > 0 &&
      hasLegacySavedCategoryId &&
      !isAnyCategoryChanged) ||
    allCategoriesAreMigrationPlaceholders
  ) {
    isMigratingCategory = true;
  }
  const handleTabClick = (index: number) => {
    setActiveTab(index);
    setFirstQuestionId?.(undefined);
    const category = categoriesToRender[index];
    logger.report(
      faqButtonClickSrc63Evid836({
        button_name: category?.id || category?.title || `${index}`,
        action: 'tab',
      }),
    );
  };

  const isDisplayCategories =
    (!searchTerm || searchTerm === '') &&
    splitIntoCategories &&
    categoriesToRender.length > 0 &&
    !isMigratingCategory;

  if (fetchError) {
    return null;
  }
  let categoriesWithLink = categoriesToRender;
  if (seo) {
    categoriesWithLink = categoriesToRender.map((category) => {
      const categoryURL = new URL(baseURL);
      categoryURL.searchParams.set('categoryId', `${category.id}`);
      categoryURL.searchParams.set('appDefId', appDefinitionId);
      return {
        ...category,
        href: categoryURL,
      };
    });
  }
  const categoriesList = categoriesWithLink.map((category, index) => {
    const { updated, created } = getCategoryDates(category);
    return {
      ...category,
      title: getCategoryTitleForOOI(
        category.id ?? '',
        index,
        category.title ?? '',
        t,
        created,
        updated,
      ),
    };
  });

  return (
    <>
      {isDisplayCategories && topCategoriesPosition && (
        <HorizontalCategories
          categoriesToRender={categoriesList as TabItem[]}
          adjustedActiveTab={activeTab}
          setActiveTab={handleTabClick}
          splitIntoCategories={!!splitIntoCategories}
          rtl={rtl || false}
        />
      )}
      {isDisplayCategories && sideCategoriesPosition && (
        <VerticalTabs
          sideCategoriesPosition={sideCategoriesPosition}
          categoriesToRender={categoriesList as TabItem[]}
          activeTab={activeTab}
          setActiveTab={handleTabClick}
        />
      )}
    </>
  );
};

export default TabsWrapper;
